import { useId } from 'react'
import type { CSSProperties, ReactNode } from 'react'

import type { DispenseRecord, Sample, SampleReport, SampleReportEvent } from '@ctcells/contract'
import { enumLabel, errorMessage, maskPii, t } from '@ctcells/i18n'

import { DataTable, type Column } from '../../atoms/DataTable.tsx'
import type { TableSort } from '../../atoms/tableSort.ts'
import { TrayFigure } from '../../figures/TrayFigure.tsx'
import { canView, type ScreenAccess } from '../access.ts'
import { dateTimeText, moduleLabelOf } from '../format.ts'
import { Card, DetailPage } from '../resourceParts.tsx'
import {
  SampleDetailScreen,
  type SampleDetailScreenProps,
  type SampleMetaPiece,
} from '../samples/SampleDetailScreen.tsx'
import { sampleIdentityHeadRows, sampleTestInfoRows } from '../samples/sampleIdentityRows.ts'
import { ValueRows } from '../../atoms/ValueRows.tsx'

import { BundleChamber, VacancyNote } from './BundleChamber.tsx'
import { bundleCardModel, sampleIdsText } from './bundleCardModel.ts'
import type { SampleBundle } from './patientChambers.ts'
import { drawerAssignedWellsOf, drawerTrayCells } from './monitoringSampleDrawerModel.ts'
import type { FlowCell } from './sampleFlowModel.ts'

import './sampleFlow.css'
import './monitoringSampleDrawer.css'

/**
 * **모니터링에서 여는 시료 서랍** — eric 2026-09-28 「시료1을 클릭할 때.. 해당 시료와 관련된 상세 내용을
 * 보이게 해줘... 챔버 두개.. 3d로 되어 있는 거 보여주고.. 그거 밑에.. 시료와 관련된 상세 내용도 나오게 채워줘」.
 *
 * ```
 * ┌ 시료 1  TEST-…-01  (진행 중)                                    [×] ┐
 * │  ┌ A1 ─────────┐  ┌ A2 ─────────┐   ← 그 시료의 챔버 전부(배정 웰 수대로) │
 * │  │  챔버 그림   │  │  챔버 그림   │                                      │
 * │  │[염색기] 진행 │  │[염색기] 진행 │   ← 카드와 같은 한 마디                 │
 * │  ── 위치 ──  (시료 목록 서랍의 「자리」와 같은 조각)                          │
 * │  ── 시료 ──  환자 번호 · 종류 · 적혈구 농도 · 채혈 날짜 · 암종 · 암 병기(값 있는 줄만) │
 * │  ── 진행 ──  시각 · 단계 · 모듈         (GET /reports/{id}.events)         │
 * │  ── 분주 기록 ──                         (GET /reports/{id}.dispenses)      │
 * └────────────────────────────────────────────────────────────────────┘
 * ```
 *
 * 🔴 **챔버는 카드와 «같은 함수 · 같은 컴포넌트»다**(`bundleCardModel` · `BundleChamber`). 서랍이 제
 *    계산을 두면 두 곳이 다른 말을 한다. 챔버 수는 **그 묶음의 웰 수**다 — 「둘」을 박지 않았다.
 * 🔴 **시료 상세는 시료 목록 서랍의 그 화면(`SampleDetailScreen`)이다** — 보기를 그대로 쓰고 위아래에 구역만 얹는다
 *    (`lead` · `trail`). 보고서 · 정정 버튼은 끈다(`hideReport` · `hideCorrection`) — 정정은 시료 목록 서랍에서 한다.
 * 🔴 **주 자료는 `GET /samples/{id}` 인데, 못 봐도 서랍은 선다.** viewer 는 403(PII 접촉면)이다 —
 *    그때도 챔버 구역은 서고 **시료 상세 구역에만** 사유가 선다. 진행·분주(보고서)는 부가 자료라
 *    실패해도 그 구역만 접는다.
 * 🔴 **값이 없는 구역은 그리지 않는다**(`—` 로 채운 줄을 깔지 않는다).
 * ⚠ 크기는 서랍 상자가 정한다(히나 — 서랍 뼈대). 여기 내용은 주어진 폭 안에서 흐르고 `.apage` 안에서 구른다.
 */

/** 챔버 구역 — 지금 도는 작업의 묶음 카드 한 장에서 온다 */
export type DrawerChambers =
  | {
      kind: 'bundle'
      bundle: SampleBundle
      /** 배치 단계 5칸 — 카드와 같은 값(`SampleFlowView.batchStages`) */
      batchStages: readonly FlowCell[]
      /** 카드 머리의 순번(대조는 없다) */
      seq?: number | undefined
      /** 구독이 끊겨 「지금」을 모른다 — 카드와 같은 문구를 그림 위에 적는다 */
      stale: boolean
    }
  /** 챔버를 못 본 사유(시료 목록 403 · 조회 중) — 카드 격자가 적는 그 문구다 */
  | { kind: 'notice'; text: string }
  /** 이 시료가 지금 도는 작업에 없다 — 챔버 구역을 세우지 않는다 */
  | { kind: 'none' }

/** 시료 상세 구역 — `GET /samples/{id}` */
export type DrawerDetail =
  | ({ kind: 'ready'; sample: Sample } & Pick<
      SampleDetailScreenProps,
      'onOpenReport' | 'reportPanel'
    >)
  | { kind: 'loading' }
  | { kind: 'notice'; text: string; tone?: 'error' | undefined }

/** 진행 · 분주 구역 — `GET /reports/{id}`(부가 자료) */
export type DrawerReport =
  | { kind: 'ready'; report: SampleReport }
  | { kind: 'loading' }
  | { kind: 'notice'; text: string }
  /** 부르지 않았다(demo) */
  | { kind: 'none' }

/**
 * 같은 카드(레시피 시료 묶음)의 **다른 시료** — 카드 한 장이 시료 둘을 보이면 서랍도 둘을 다 보인다
 * (eric 2026-10-14 「시료4를 클릭하면.. 두개가 보여야 하는데.. 왜 하나만 보여?」).
 */
export interface DrawerMember {
  sampleId: string
  /** 시료 목록(`GET /samples`)에서 찾은 그 시료. 못 찾았거나 못 봤으면 사유 */
  detail: { kind: 'ready'; sample: Sample } | { kind: 'notice'; text: string }
  report: DrawerReport
}

export interface MonitoringSampleDrawerProps {
  sampleId: string
  /** 같은 카드의 다른 시료들(카드 차례). 없거나 비면 시료 하나짜리 서랍이다(종전) */
  members?: readonly DrawerMember[] | undefined
  access: ScreenAccess
  onClose: () => void
  chambers: DrawerChambers
  detail: DrawerDetail
  report: DrawerReport
}

/**
 * 모니터링이 이미 말하는 조각 — 작업은 상시 작업 줄이, 단계 · 현재 모듈은 챔버 아래 한 마디가 말한다.
 * 등록 시각 · 버전도 뺀다(eric 2026-10-06 「등록 시각은 빼줘」 · 「v3 버전 삭제해주고」).
 */
const MONITORING_OMIT: readonly SampleMetaPiece[] = [
  'batch',
  'stage',
  'module',
  'registered',
  'version',
]

export function MonitoringSampleDrawer({
  sampleId,
  members = [],
  access,
  onClose,
  chambers,
  detail,
  report,
}: MonitoringSampleDrawerProps) {
  const seq = chambers.kind === 'bundle' ? chambers.seq : undefined
  const titleLead =
    seq === undefined ? undefined : t('monitoring.flow.groups.patient', { index: String(seq) })
  const lead = <DrawerChamberSection chambers={chambers} />
  /*
   * 🔴 **묶음이면 시료마다 제 카드 · 제 기록이다** — 제목은 묶음 번호 전부(카드 머리와 같은 `sampleIdsText`),
   *    「시료」 카드 · 진행 · 분주 기록은 시료 번호를 붙여 시료마다 선다. 한 시료의 값으로 다른 시료를 대신하지 않는다.
   */
  const grouped = members.length > 0
  const visibleIds = canView(access)
  const titleText = grouped
    ? sampleIdsText([sampleId, ...members.map((m) => m.sampleId)])
    : undefined
  const tagOf = (id: string) => (grouped ? maskPii(id, visibleIds) : undefined)
  const trail = (
    <>
      {members.map((member) => (
        <MemberSampleCard key={member.sampleId} member={member} visible={visibleIds} />
      ))}
      <DrawerReportSections report={report} tag={tagOf(sampleId)} />
      {members.map((member) => (
        <DrawerReportSections
          key={member.sampleId}
          report={member.report}
          tag={tagOf(member.sampleId)}
        />
      ))}
    </>
  )

  if (detail.kind === 'ready') {
    return (
      <SampleDetailScreen
        access={access}
        /* 🔴 「배정 웰」 줄은 챔버와 같은 출처의 웰 전부(eric 2026-09-28 「배정웰이 두개인데.. 왜 하나만」) */
        assignedWells={drawerAssignedWellsOf(chambers)}
        /* 🔴 시료 정보를 본문 구역으로(eric 2026-09-28 「혈액 환자.. 무슨 암..」) — 카드에 환자·종류가 없다. 자리 구역 아래다 */
        identityInBody
        /*
         * 🔴 보고서 · 정정 버튼 둘 다 없다(eric 2026-10-02 「보고서 버튼 빼고」 → 2026-10-06 「정정 버튼 삭제하고」) — 보는 화면이다.
         *    정정은 작업 준비 › 시료 서랍에서 한다. 상태는 칩(「진행 중.. 칩으로」)
         */
        hideCorrection
        hideReport
        statusChip
        lead={lead}
        omitMeta={MONITORING_OMIT}
        onClose={onClose}
        /* 트레이 바로 아래가 자리(현재 자리 · 배정 웰)다 — 시료 정보는 그 아래(eric 2026-10-06) */
        placementFirst
        placementTitle={t('card.location')}
        samples={[detail.sample]}
        titleLead={titleLead}
        {...(titleText === undefined ? {} : { titleText })}
        {...(grouped
          ? { identityTitle: `${t('card.sample')} · ${maskPii(sampleId, visibleIds)}` }
          : {})}
        trail={trail}
        {...(detail.onOpenReport === undefined ? {} : { onOpenReport: detail.onOpenReport })}
        {...(detail.reportPanel === undefined ? {} : { reportPanel: detail.reportPanel })}
      />
    )
  }

  /*
   * 🔴 **시료 상세를 못 봤어도 서랍은 선다.** 머리(순번 · 시료 번호 · ×)와 챔버 구역이 그대로이고
   *    「시료」 구역 한 자리에만 사유가 선다(진행 · 분주 기록도 제 구역에 제 사유 — `DrawerReportSections`).
   */
  const visible = visibleIds
  const heading = maskPii(titleText ?? sampleId, visible)
  return (
    <DetailPage
      onClose={onClose}
      title={
        titleLead === undefined ? (
          heading
        ) : (
          <>
            <span className="ahead__lead">{titleLead}</span> {heading}
          </>
        )
      }
    >
      <div className="fstack">
        {lead}
        {/* 준비된 서랍의 첫 구역(`SampleDetailScreen` identityInBody)과 같은 이름 — 같은 자리에 사유만 선다 */}
        <Card
          title={grouped ? `${t('card.sample')} · ${maskPii(sampleId, visible)}` : t('card.sample')}
        >
          <p
            className={detail.kind === 'notice' && detail.tone === 'error' ? 'vissue' : 'msd-note'}
            role={detail.kind === 'notice' && detail.tone === 'error' ? 'alert' : 'status'}
          >
            {detail.kind === 'loading' ? t('state.loading') : detail.text}
          </p>
        </Card>
        {trail}
      </div>
    </DetailPage>
  )
}

/**
 * **챔버 구역** — 그 시료가 트레이의 어느 칸에 있나를 **트레이 한 장**으로(eric 2026-10-06 「챔버 말고.. 트레이를 그려달라고」).
 *
 * 🔴 그 시료의 웰만 챔버가 든 칸 · 나머지는 빈 자리다(`drawerTrayCells`). 모양은 장비 탭 트레이(`chamberOutline`)와 같다.
 *    단계 한 마디(「염색기 · 진행」)는 두지 않는다(eric 2026-10-06 「염색기 진행 이거 삭제해줘」).
 * ⚠ 웰 이름이 트레이 칸이 아니면 칸을 지어내지 않고 종전 챔버 그림(`BundleChamber`)으로 남는다.
 * 🔴 **PII 가 없다** — 웰 이름 · 모듈 · 단계 낱말뿐이다. 시료 번호는 머리에만 있고 거기서 가린다.
 */
function DrawerChamberSection({ chambers }: { chambers: DrawerChambers }) {
  const headId = useId()
  if (chambers.kind === 'none') return null
  if (chambers.kind === 'notice') {
    return (
      <section aria-labelledby={headId} className="msd-ch">
        <h3 className="a-sr" id={headId}>
          {t('monitoring.sampleDrawer.chambers')}
        </h3>
        <p className="msd-note" role="status">
          {chambers.text}
        </p>
      </section>
    )
  }

  const { bundle, batchStages, stale } = chambers
  const { phase, vacancy } = bundleCardModel(bundle, batchStages)
  const trayCells = drawerTrayCells(bundle)
  const wellsText = bundle.chambers.map((dot) => dot.well).join(' · ')
  return (
    <section aria-labelledby={headId} className="msd-ch">
      <h3 className="a-sr" id={headId}>
        {t('monitoring.sampleDrawer.chambers')}
      </h3>
      {bundle.role === 'reference' ? (
        <span className="bundle__tag msd-ch__tag">{t('monitoring.reference')}</span>
      ) : null}
      {/* 🔴 카드와 같은 문구 — 마지막으로 안 것을 현재처럼 보이지 않는다 */}
      {stale ? (
        <p className="bundles__stale" role="status">
          {t('monitoring.flow.stale')}
        </p>
      ) : null}
      {bundle.chambers.length === 0 ? (
        /* 🔴 묶음에 못 든 시료 — 단계 한 마디는 서랍에 두지 않는다(eric 2026-10-06) */
        <p className="bundle__nochamber msd-ch__none">{t('monitoring.flow.groups.noChamber')}</p>
      ) : trayCells !== undefined ? (
        <div className="msd-ch__tray">
          <TrayFigure
            cells={trayCells}
            chamberOutline
            label={t('monitoring.sampleDrawer.tray', { wells: wellsText })}
          />
          {/* 🔴 단계 한 마디(「염색기 · 진행」)는 두지 않는다(eric 2026-10-06 「염색기 진행 이거 삭제해줘」) */}
          {vacancy === undefined ? null : <VacancyNote word={vacancy} />}
        </div>
      ) : (
        <div className="msd-ch__wrap">
          <div
            className="msd-ch__list"
            data-count={bundle.chambers.length}
            style={{ '--msd-cols': String(bundle.chambers.length) } as CSSProperties}
          >
            {bundle.chambers.map((dot) => (
              <BundleChamber
                dot={dot}
                key={dot.well}
                {...(vacancy === undefined ? {} : { vacancy })}
                {...(phase === undefined ? {} : { phase })}
              />
            ))}
          </div>
        </div>
      )}
    </section>
  )
}

/**
 * **진행 · 분주 기록** — 둘 다 `GET /reports/{sample_id}` 한 응답에서 온다(요청 1건 · 모니터링 카드가
 * 이미 같은 캐시 키로 받아 둔다 — `useSampleReports`).
 * ⚠ `GET /dispense-records?sample_id=` 는 **같은 표(`dispense_log`)를 같은 조건으로** 읽는다
 *   (`app/services/reports.py` `_dispenses`) — 요청을 하나 더 내지 않는다.
 * ⚠ `GET /history` 에는 시료로 거르는 파라미터가 없다(`batch_id` 뿐 · 2026-09-28 `openapi.json`).
 */
/**
 * 묶음의 다른 시료 한 장 — 첫 시료의 「시료」 카드(`SampleDetailScreen` identityInBody)와 같은 줄 · 같은 차례(값 있는 줄만).
 */
function MemberSampleCard({ member, visible }: { member: DrawerMember; visible: boolean }) {
  const title = `${t('card.sample')} · ${maskPii(member.sampleId, visible)}`
  if (member.detail.kind === 'notice')
    return (
      <Card title={title}>
        <p className="msd-note" role="status">
          {member.detail.text}
        </p>
      </Card>
    )
  const { sample } = member.detail
  const rows = [
    ...sampleIdentityHeadRows(sample, visible),
    ...sampleTestInfoRows(sample).filter((row) => row.value !== ''),
  ]
  return <Card title={title}>{rows.length === 0 ? null : <ValueRows rows={rows} />}</Card>
}

function DrawerReportSections({
  report,
  tag,
}: {
  report: DrawerReport
  /** 묶음이면 시료 번호 — 구역 제목에 붙는다(「진행 · TEST-…-07」) */
  tag?: string | undefined
}) {
  const titled = (text: string) => (tag === undefined ? text : `${text} · ${tag}`)
  if (report.kind === 'none' || report.kind === 'loading') return null
  if (report.kind === 'notice') {
    /*
     * 🔴 **두 구역 다 한 응답에서 온다** — 못 받았으면 진행도 분주 기록도 모른다. 각 구역에 제 사유를 적는다
     *    (viewer 「권한이 없습니다」 · 2026-09-28 팀장 지시 「그 구역에만」). 한 곳에 몰아 적으면 다른 구역이
     *    «없는 것»처럼 읽힌다.
     */
    return (
      <>
        {(['monitoring.sampleDrawer.progress', 'monitoring.sampleDrawer.dispenses'] as const).map(
          (key) => (
            <Card key={key} title={titled(t(key))}>
              <p className="msd-note" role="status">
                {report.text}
              </p>
            </Card>
          ),
        )}
      </>
    )
  }
  const { report: data } = report
  /*
   * 🔴 **그 시료의 «지금 배치» 사건만** — 보고서 `events` 는 배치로 걸러 오지 않아 시료가 갈아탄
   *    이전 회차의 사건이 섞인다(`SampleReportEvent.batch_id` 설명). 배치가 없는 시료는 전부다.
   */
  const events = data.events.filter(
    (event) => data.batch_id == null || event.batch_id === data.batch_id,
  )
  const dispenses = data.dispenses.filter(
    (row) => data.batch_id == null || row.batch_id == null || row.batch_id === data.batch_id,
  )
  return (
    <>
      {events.length === 0 ? null : (
        <Card title={titled(t('monitoring.sampleDrawer.progress'))}>
          <ProgressTable events={events} />
        </Card>
      )}
      {dispenses.length === 0 ? null : (
        <Card title={titled(t('monitoring.sampleDrawer.dispenses'))}>
          <DispenseTable rows={dispenses} />
          {data.reagent_lots_used.length === 0 ? null : (
            <p className="fsec__help">{`${t('row.reagentLots')} · ${data.reagent_lots_used.join(' · ')}`}</p>
          )}
        </Card>
      )}
    </>
  )
}

/**
 * 진행 · 분주 두 표의 처음 정렬 — **시각 최신 위**(eric 2026-09-30 「모든 게시판」 머리글 정렬).
 * ⚠ 서버(`app/services/reports.py`)는 두 목록을 `time` **오름차순**으로 준다 — 처음 차례가 받은 차례와 반대다.
 *   서랍은 「지금 어디까지 왔나」를 보는 자리라 최신이 위다. 머리글을 한 번 더 누르면 받은 차례(오래된 것 위)다.
 */
const REPORT_DEFAULT_SORT: TableSort = { key: 'time', direction: 'desc' }

function ProgressTable({ events }: { events: readonly SampleReportEvent[] }) {
  /* 🔴 실패 코드 열은 **한 건이라도 있을 때만** 선다 — 전부 빈 열을 깔지 않는다 */
  const withError = events.some((event) => event.error_code != null)
  const columns: Column<SampleReportEvent>[] = [
    {
      key: 'time',
      header: t('column.time'),
      sort: 'time',
      sortValue: (e) => e.time,
      cell: (e) => dateTimeText(e.time),
      nowrap: true,
    },
    {
      key: 'stage',
      header: t('column.step'),
      sort: 'text',
      sortValue: (e) => (e.stage == null ? null : enumLabel('enum.sampleStage', e.stage)),
      cell: (e) => (e.stage == null ? '' : enumLabel('enum.sampleStage', e.stage)),
      nowrap: true,
    },
    {
      key: 'module',
      header: t('column.module'),
      sort: 'text',
      sortValue: (e) => moduleLabelOf(e.module),
      cell: (e) => moduleLabelOf(e.module),
    },
    ...(withError
      ? [
          {
            key: 'error',
            header: t('column.result'),
            sort: 'text' as const,
            sortValue: (e: SampleReportEvent) =>
              e.error_code == null ? null : errorMessage({ error_code: e.error_code }),
            cell: (e: SampleReportEvent): ReactNode =>
              e.error_code == null ? '' : errorMessage({ error_code: e.error_code }),
          },
        ]
      : []),
  ]
  return (
    <DataTable
      caption={t('monitoring.sampleDrawer.progress')}
      columns={columns}
      defaultSort={REPORT_DEFAULT_SORT}
      density="compact"
      rowKey={(e) => `${e.time ?? ''}-${e.stage ?? ''}-${e.module ?? ''}`}
      rows={events}
    />
  )
}

function DispenseTable({ rows }: { rows: readonly DispenseRecord[] }) {
  const columns: Column<DispenseRecord>[] = [
    {
      key: 'time',
      header: t('column.time'),
      sort: 'time',
      sortValue: (r) => r.time,
      cell: (r) => dateTimeText(r.time),
      nowrap: true,
    },
    {
      key: 'step',
      header: t('column.step'),
      sort: 'text',
      sortValue: (r) => r.step_ref,
      cell: (r) => r.step_ref,
      wrapAnywhere: true,
    },
    {
      key: 'target',
      header: t('monitoring.supply.column.target'),
      sort: 'text',
      sortValue: (r) => r.wellName,
      cell: (r) => r.wellName,
      nowrap: true,
    },
    {
      key: 'volume',
      header: t('monitoring.supply.column.volume'),
      // 부피(µL)로 세운다 — 동작(흡인 · 분주)은 칸에 같이 적히지만 차례의 축이 아니다
      sort: 'number',
      sortValue: (r) => r.volume,
      cell: (r) =>
        [
          r.operation == null ? '' : t(`monitoring.supply.op.${r.operation}`),
          `${String(r.volume)} µL`,
        ]
          .filter((part) => part !== '')
          .join(' '),
      nowrap: true,
      align: 'end',
    },
    {
      key: 'result',
      header: t('column.result'),
      sort: 'text',
      sortValue: (r) => (r.result == null ? null : t(`monitoring.supply.result.${r.result}`)),
      cell: (r) => (r.result == null ? '' : t(`monitoring.supply.result.${r.result}`)),
      nowrap: true,
    },
  ]
  return (
    <DataTable
      caption={t('monitoring.sampleDrawer.dispenses')}
      columns={columns}
      defaultSort={REPORT_DEFAULT_SORT}
      density="compact"
      rowKey={(r) => r.dispense_id}
      rows={rows}
    />
  )
}
